import { CSSProperties, useEffect, useMemo, useState } from "react";

import { Callout, Classes, Icon } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { Link, useParams } from "react-router";

import { Box, BoxProps, Flex } from "@/components/layout/flexbox";
import { isXlQuery } from "@/components/layout/Media";
import { RoleplaySessionHeader } from "@/components/pages_logged_in/roleplays/details/RoleplaySessionHeader";
import { ScenarioAssignmentInfo } from "@/components/pages_logged_in/roleplays/details/scenario/ScenarioAssignmentInfo";
import { ScenarioHistory } from "@/components/pages_logged_in/roleplays/details/scenario/ScenarioHistory";
import {
  FEEDBACK_NAVIGATION_WIDTH,
  MAX_FEEDBACK_CONTAINER_WIDTH,
} from "@/components/pages_logged_in/roleplays/details/scenario/session/constants";
import { RoleplaySessionTranscript } from "@/components/pages_logged_in/roleplays/details/scenario/session/RoleplaySessionTranscript";
import {
  formatMessagesData,
  prepareEvaluationCriteria,
} from "@/components/pages_logged_in/roleplays/details/scenario/session/utils";
import { LoadingStars } from "@/components/pages_logged_in/roleplays/LoadingStar";
import { buildScenarioUrl } from "@/components/pages_logged_in/roleplays/utils";
import { Container } from "@/components/pieces/bootstrap";
import { CardAboveXs } from "@/components/pieces/CardAboveXs";
import { CollapsibleFeedback } from "@/components/pieces/roleplays/CollapsibleFeedback";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import ScenarioSimpleCard from "@/components/pieces/roleplays/ScenarioSimpleCard";
import { getUser } from "@/components/session/JwtTokenManager";
import { Button } from "@/components/ui/button";
import { SIDE_NAV_WIDTH } from "@/css/constants";
import {
  RoleplaySessionProcessingStatus,
  RoleplaySessionTranscriptFragment,
  useRoleplayQuery,
  useRoleplaySessionTranscriptQuery,
} from "@/graphql";
import { FullRoleplaySession } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { AICoachBar } from "./AICoachBar";
import { RoleplayFeedbacks } from "./RoleplayFeedbacks";
import { RoleplaySessionInvalidAttemptMessageBox } from "./RoleplaySessionInvalidAttemptMessageBox";
import { RoleplaySessionQuiz } from "./RoleplaySessionQuiz";
import { Summary } from "./Summary";

const waitingMessages = [
  "Reading the transcript...",
  "Scanning for key points...",
  "Beginning initial analysis...",
  "Processing raw data...",
  "Identifying patterns...",
  "Gathering preliminary insights...",
  "Running performance metrics...",
  "Calculating key indicators...",
  "Analyzing speaking patterns...",
  "Evaluating delivery style...",
  "Cross-referencing data points...",
  "Synthesizing observations...",
  "Mapping improvement areas...",
  "Generating detailed metrics...",
  "Developing initial feedback...",
  "Formulating recommendations...",
  "Building action items...",
  "Organizing insights...",
  "Structuring feedback points...",
  "Refining observations...",
  "Enhancing clarity...",
  "Writing detailed feedback...",
  "Polishing analysis...",
  "Reviewing suggestions...",
  "Cross-checking accuracy...",
  "Adding contextual notes...",
  "Incorporating final insights...",
  "Formatting recommendations...",
  "Finalizing assessment...",
  "Validating conclusions...",
  "Preparing delivery format...",
  "Assembling final report...",
  "Running quality checks...",
  "Preparing to deliver results...",
];

function LoadingState() {
  return (
    <>
      <Box className={Classes.SKELETON} height={350} />
      <Box className={Classes.SKELETON} height={350} mt={24} />
    </>
  );
}

interface CustomSkeletonProps extends BoxProps {
  height?: number;
  width?: number | string;
  style?: CSSProperties;
}

function CustomSkeleton({
  height = 16,
  width,
  style = {},
  ...props
}: CustomSkeletonProps) {
  return (
    <Box
      className={Classes.SKELETON}
      height={height}
      width={width}
      style={style}
      {...props}
    />
  );
}

interface SkeletonGroupProps {
  opacity?: number;
  mt?: number | string;
  count?: number;
  height?: number;
}

function SkeletonGroup({
  opacity = 1,
  mt,
  count = 1,
  height = 16,
}: SkeletonGroupProps) {
  return (
    <Box opacity={opacity}>
      {[...Array(count)].map((_, index) => (
        <CustomSkeleton key={index} height={height} mt={index === 0 ? mt : 2} />
      ))}
    </Box>
  );
}

function FeedbackLoadingCard({ count = 4 }: { count?: number }) {
  return (
    <Flex
      p={"24px"}
      style={{ flex: 1, backgroundColor: "rgba(244, 246, 252, 0.5)" }}
      flexDirection="column"
    >
      <CustomSkeleton width={160} />
      <SkeletonGroup opacity={0.5} mt={16} count={count} />
    </Flex>
  );
}

function SectionWithSkeletons({
  mt = "32px",
  skeletonHeight = 48,
}: {
  mt?: string;
  skeletonHeight?: number;
}) {
  return (
    <Box mt={mt}>
      <CustomSkeleton width={160} />
      <SkeletonGroup
        opacity={0.5}
        mt={"12px"}
        count={2}
        height={skeletonHeight}
      />
    </Box>
  );
}

function LoadingFeedbackSkeleton() {
  return (
    <Box style={{ position: "relative" }}>
      {/* Summary Section */}
      <Box className="summary">
        <Flex alignItems="center" style={{ gap: 24 }}>
          <Box
            mb={0}
            style={{
              filter: "grayscale(1)",
            }}
          >
            <RankIcon rank="NotStarted" type="circle" size={45} />
          </Box>
          <Box style={{ flex: 1 }}>
            <CustomSkeleton width={160} />
            <CustomSkeleton
              height={40}
              mt={"10px"}
              style={{
                borderRadius: "100px",
                opacity: 0.5,
              }}
            />
          </Box>
        </Flex>
      </Box>

      {/* Feedback Overview */}
      <Box mt={"24px"}>
        <FeedbackLoadingCard count={2} />
      </Box>

      {/* Feedback Cards */}
      <Box mt={"24px"}>
        <Flex alignItems="center" style={{ gap: 24, borderRadius: 8 }}>
          <FeedbackLoadingCard />
          <FeedbackLoadingCard />
        </Flex>
      </Box>

      {/* Additional Sections */}
      <SectionWithSkeletons />
      <SectionWithSkeletons />

      {/* Gradient Overlay */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "320px",
          background: "linear-gradient(to bottom, rgba(255,255,255,0), white)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

interface AnalyzingStateProps {
  roleplaySession: FullRoleplaySession;
  showHistory: boolean;
}

function AnalyzingState({ roleplaySession, showHistory }: AnalyzingStateProps) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % waitingMessages.length);
    }, 7000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Box mt={24}>
        <Flex
          className="bas round-corners"
          backgroundColor="#FFF"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mt={4}
          py={"66px"}
          px={0}
        >
          <LoadingStars />
          <Box mt={3} fontSize={20} fontWeight={600} textAlign="center">
            Preparing Insights
          </Box>
          <Box mt={1} fontSize={16} fontWeight={400} textAlign="center">
            {waitingMessages[messageIndex]}
          </Box>
        </Flex>
      </Box>
      <Box
        mt={24}
        backgroundColor="#FFF"
        py={"66px"}
        p={"24px"}
        className="bas round-corners"
      >
        <LoadingFeedbackSkeleton />
      </Box>
      {showHistory && (
        <>
          <Box className="bts" my={5} />
          <Box mb={5}>
            <CardAboveXs>
              {roleplaySession?.scenario && (
                <ScenarioHistory
                  scenario={roleplaySession.scenario}
                  excludeSessionUuid={roleplaySession.uuid}
                />
              )}
            </CardAboveXs>
          </Box>
        </>
      )}
    </>
  );
}

interface ContentInteriorProps {
  roleplaySession: FullRoleplaySession;
}

function ContentInterior({ roleplaySession }: ContentInteriorProps) {
  // Memoize the evaluation criteria preparation to avoid redundant processing on re-renders
  // This is an expensive operation that involves deep cloning, filtering, sorting and optimizing
  const sortedGroupedCriteria =
    useMemo(() => {
      // Prepare evaluation criteria - sorting groups, evaluations, and ensuring
      // first quotes shown in each feedback card are as unique as possible
      if (roleplaySession?.evaluations) {
        const nonNullEvaluations = ensureNonNullArray(
          roleplaySession.evaluations,
        );
        return prepareEvaluationCriteria(nonNullEvaluations);
      }
    }, [roleplaySession]) || [];

  if (!roleplaySession) return null;

  return (
    <>
      <CardAboveXs mt={24}>
        <Summary roleplaySession={roleplaySession} />
        {sortedGroupedCriteria.map(({ name, evaluations }) => (
          <Box key={name} mt={48}>
            <Box fontWeight={500} fontSize={18}>
              {name}
            </Box>
            {evaluations.map((evaluation) => (
              <CollapsibleFeedback
                key={evaluation.id}
                evaluation={evaluation}
              />
            ))}
          </Box>
        ))}
      </CardAboveXs>
    </>
  );
}

interface RoleplaySessionContentProps {
  roleplaySession?: FullRoleplaySession;
  view?: string;
  loading?: boolean;
  errorMsg?: string;
}

export function RoleplaySessionContent({
  roleplaySession,
  view,
  loading,
  errorMsg,
}: RoleplaySessionContentProps) {
  const [selectedMessage, setSelectedMessage] =
    useState<RoleplaySessionTranscriptFragment>();

  const user = getUser();
  const isSessionOwner = roleplaySession?.user?.id === user?.id;
  const [seekTime, setSeekTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const isXl = useMediaQuery(isXlQuery);

  const handleTimeChange = (event: Event, duration: number) => {
    const audioElement = event.target as HTMLAudioElement;
    setSeekTime(audioElement?.currentTime);
    setDuration(duration);
  };

  const handleSelectTime = (time: number) => {
    setCurrentTime(time);
  };

  const { isValidAttempt } = roleplaySession || {};
  const isAnalyzing =
    roleplaySession &&
    roleplaySession.processingStatus !==
      RoleplaySessionProcessingStatus.Processed;

  const isTranscriptView = view === "transcript";
  const showInvalidAttemptMessageBox =
    !loading && !isAnalyzing && !isTranscriptView && isValidAttempt === false;
  const feedbacks = ensureNonNullArray(selectedMessage?.feedbacks);

  const showFeedbackSidebar = feedbacks.length > 0 && isXl;

  const roleplaySlug = useParams<{
    roleplaySlug?: string;
  }>()?.roleplaySlug;

  const scenarioUrl = buildScenarioUrl({
    roleplaySlug,
    scenarioSlug: roleplaySession?.scenario?.slug || "",
  });

  const { data } = useRoleplayQuery({
    variables: {
      slug: roleplaySlug,
    },
    skip: !roleplaySlug,
  });

  const { data: roleplaySessionTranscriptQueryData, loading: isLoading } =
    useRoleplaySessionTranscriptQuery({
      fetchPolicy: "no-cache", // fix graphql caching wrong feedbacks issue for each feedback line
      skip: !roleplaySession?.uuid,
      variables: {
        uuid: roleplaySession?.uuid || "",
      },
    });

  const messagesData = useMemo(() => {
    if (
      roleplaySessionTranscriptQueryData?.roleplaySession &&
      roleplaySession
    ) {
      const session = {
        ...roleplaySession,
        ...roleplaySessionTranscriptQueryData.roleplaySession,
      };
      return formatMessagesData({
        session: {
          transcripts: session.transcripts || [],
          scenario: {
            personaName: session.scenario?.personaName || "",
            personaPhotoUrl: session.scenario?.personaPhotoUrl || "",
          },
          user: {
            name: session.user?.name || "",
            photo: session.user?.photo || "",
          },
        },
        isSessionOwner,
      });
    }
  }, [
    roleplaySession,
    isSessionOwner,
    roleplaySessionTranscriptQueryData?.roleplaySession,
  ]);

  const handleNavigateMessageFeedbacks = (direction: "up" | "down") => {
    if (!selectedMessage || !messagesData) return;

    const messagesWithFeedback = messagesData.filter((msg) =>
      Boolean(msg.feedbacks?.length),
    );

    const currentIndex = messagesWithFeedback.findIndex(
      (msg) => msg.id === selectedMessage.id,
    );

    if (currentIndex === -1) return;

    let nextIndex: number;
    if (direction === "up") {
      nextIndex =
        currentIndex > 0 ? currentIndex - 1 : messagesWithFeedback.length - 1;
    } else {
      nextIndex =
        currentIndex < messagesWithFeedback.length - 1 ? currentIndex + 1 : 0;
    }

    setSelectedMessage(messagesWithFeedback[nextIndex]);
  };

  const roleplay = data?.roleplay;

  return (
    <Flex ml={[0, 0, SIDE_NAV_WIDTH]}>
      <Box
        width="100%"
        maxWidth={
          showFeedbackSidebar
            ? `calc(100% - ${MAX_FEEDBACK_CONTAINER_WIDTH}px)`
            : "100%"
        }
        css={{ position: "relative" }}
      >
        <RoleplaySessionHeader
          roleplay={roleplay ?? undefined}
          scenario={roleplaySession?.scenario}
          roleplaySession={roleplaySession}
          view={view}
          loading={loading}
          px={showFeedbackSidebar ? FEEDBACK_NAVIGATION_WIDTH + 12 : 0}
        />
        {roleplaySession?.scenario?.id && (
          <ScenarioAssignmentInfo scenarioId={roleplaySession?.scenario?.id} />
        )}
        <Container
          px={showFeedbackSidebar ? FEEDBACK_NAVIGATION_WIDTH + 12 : 15}
        >
          <Box my={[24, 36]}>
            <Box mb={16}>
              {roleplaySession && !isSessionOwner && (
                <Callout intent="primary" icon={null} style={{ fontSize: 14 }}>
                  💡 You are viewing <b>{roleplaySession?.user?.name}</b>'s
                  session
                </Callout>
              )}
            </Box>

            {loading && <LoadingState />}

            {showInvalidAttemptMessageBox && roleplaySession && (
              <RoleplaySessionInvalidAttemptMessageBox
                roleplaySession={roleplaySession}
              />
            )}

            {!showInvalidAttemptMessageBox && (
              <RoleplaySessionSuccessContent
                roleplaySession={roleplaySession}
                isTranscriptView={isTranscriptView}
                isSessionOwner={isSessionOwner}
                isAnalyzing={isAnalyzing}
                scenarioUrl={scenarioUrl}
                loading={loading}
                errorMsg={errorMsg}
              />
            )}

            {!loading && isTranscriptView && (
              <RoleplaySessionTranscript
                selectedMessage={selectedMessage}
                onSelectMessage={(message) => setSelectedMessage(message)}
                currentTime={currentTime}
                onTimeChange={handleTimeChange}
                messagesData={messagesData || []}
                isLoading={isLoading}
                seekTime={seekTime}
                duration={duration}
                roleplaySession={
                  roleplaySessionTranscriptQueryData?.roleplaySession
                }
                onSelectTime={handleSelectTime}
              />
            )}

            {isSessionOwner &&
              !isTranscriptView &&
              roleplaySession?.scenario && (
                <>
                  <Box className="bts" my={5} />
                  <Box mb={5}>
                    <CardAboveXs>
                      <ScenarioHistory
                        scenario={roleplaySession?.scenario}
                        excludeSessionUuid={roleplaySession?.uuid}
                      />
                    </CardAboveXs>
                  </Box>
                </>
              )}
          </Box>
        </Container>
      </Box>
      {selectedMessage && feedbacks.length > 0 && (
        <RoleplayFeedbacks
          selectedMessage={selectedMessage}
          onClose={() => setSelectedMessage(undefined)}
          onNavigate={handleNavigateMessageFeedbacks}
        />
      )}
      {!loading &&
        !isTranscriptView &&
        roleplaySession &&
        roleplaySession.processingStatus ===
          RoleplaySessionProcessingStatus.Processed &&
        roleplaySession.isValidAttempt !== false &&
        isSessionOwner && <AICoachBar roleplaySession={roleplaySession} />}
    </Flex>
  );
}

interface RoleplaySessionSuccessContentProps {
  roleplaySession?: FullRoleplaySession;
  isTranscriptView: boolean;
  isSessionOwner: boolean;
  isAnalyzing?: boolean;
  scenarioUrl?: string;
  loading?: boolean;
  errorMsg?: string;
}

function RoleplaySessionSuccessContent({
  roleplaySession,
  isTranscriptView,
  isSessionOwner,
  isAnalyzing,
  scenarioUrl,
  loading,
  errorMsg,
}: RoleplaySessionSuccessContentProps) {
  if (isTranscriptView) return null;

  const retryButton =
    isSessionOwner && scenarioUrl ? (
      <Button asChild intent="secondary" className="h-10">
        <Link to={scenarioUrl}>
          <Icon icon="reset" />
          Retry
        </Link>
      </Button>
    ) : null;

  return (
    <>
      {roleplaySession?.scenario && (
        <ScenarioSimpleCard
          scenario={roleplaySession.scenario}
          showDifficulty={true}
          showShortDesc={true}
          isInteractive={false}
          rightContent={retryButton}
        />
      )}

      {!!roleplaySession?.formFields?.length && (
        <RoleplaySessionQuiz
          roleplaySession={roleplaySession}
          isProgramLeadView={!isSessionOwner}
        />
      )}

      {!loading && errorMsg && (
        <Callout intent="danger" icon="warning-sign">
          {errorMsg}
        </Callout>
      )}

      {!loading &&
        roleplaySession &&
        (isAnalyzing ? (
          <AnalyzingState roleplaySession={roleplaySession} showHistory />
        ) : (
          <ContentInterior roleplaySession={roleplaySession} />
        ))}
    </>
  );
}
